/**
 * Common App Integration Service
 * 
 * Handles API calls for importing profile data from Common Application.
 * 
 * Security Notes:
 * - Credentials are only sent to our backend, never stored in frontend
 * - All API calls are authenticated with user's JWT token
 * - Sensitive operations are logged for audit purposes
 */

import api from '@/utils/api.utils';

export interface CommonAppCredentials {
  email: string;
  password: string;
}

export interface CommonAppImportRequest extends CommonAppCredentials {
  overwrite_existing?: boolean;
  selective_import?: string[];
}

export interface CommonAppImportResponse {
  success: boolean;
  message: string;
  imported_fields: string[];
  import_date?: string;
  total_imported: number;
  errors?: string[];
}

export interface CommonAppValidationResponse {
  success: boolean;
  credentials_valid: boolean;
  message: string;
}

export interface ImportableFieldsResponse {
  success: boolean;
  importable_fields: {
    personal_information: string[];
    academic_information: string[];
    test_scores: string[];
    activities: string[];
  };
  total_fields: number;
}

export interface ImportHistory {
  success: boolean;
  import_history: Array<{
    import_date: string;
    import_source: string;
    status: string;
  }>;
  total_imports: number;
  last_import?: string;
}

class CommonAppService {
  private readonly baseUrl = '/api/v1/commonapp-integration';

  /**
   * Get list of fields that can be imported from Common App
   */
  async getImportableFields(): Promise<ImportableFieldsResponse> {
    try {
      const response = await api.get(`${this.baseUrl}/importable-fields`);
      return response.data;
    } catch (error) {
      console.error('Error fetching importable fields:', error);
      throw new Error('Failed to fetch importable fields');
    }
  }

  /**
   * Validate Common App credentials without importing data
   */
  async validateCredentials(credentials: CommonAppCredentials): Promise<CommonAppValidationResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/validate-credentials`, credentials);
      return response.data;
    } catch (error: any) {
      console.error('Error validating credentials:', error);
      
      if (error.response?.status === 429) {
        throw new Error('Too many validation attempts. Please try again later.');
      }
      
      throw new Error('Failed to validate credentials');
    }
  }

  /**
   * Import profile data from Common App
   */
  async importProfile(importRequest: CommonAppImportRequest): Promise<CommonAppImportResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/import-profile`, importRequest);
      return response.data;
    } catch (error: any) {
      console.error('Error importing profile:', error);
      
      if (error.response?.status === 429) {
        throw new Error('Too many import attempts. Please try again in an hour.');
      }
      
      if (error.response?.status === 401) {
        throw new Error('Invalid Common App credentials.');
      }
      
      throw new Error('Failed to import profile data');
    }
  }

  /**
   * Get user's Common App import history
   */
  async getImportHistory(): Promise<ImportHistory> {
    try {
      const response = await api.get(`${this.baseUrl}/import-history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching import history:', error);
      throw new Error('Failed to fetch import history');
    }
  }

  /**
   * Clear all imported Common App data
   */
  async clearImportedData(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`${this.baseUrl}/clear-import-data`);
      return response.data;
    } catch (error) {
      console.error('Error clearing imported data:', error);
      throw new Error('Failed to clear imported data');
    }
  }

  /**
   * Get preview of what would be imported (without actually importing)
   * This is a conceptual method - would need backend implementation
   */
  async getImportPreview(credentials: CommonAppCredentials): Promise<any> {
    try {
      const response = await api.post(`${this.baseUrl}/import-preview`, credentials);
      return response.data;
    } catch (error) {
      console.error('Error getting import preview:', error);
      throw new Error('Failed to get import preview');
    }
  }

  /**
   * Format field names for display
   */
  formatFieldName(fieldName: string): string {
    const fieldMappings: Record<string, string> = {
      'first_name': 'First Name',
      'middle_name': 'Middle Name',
      'last_name': 'Last Name',
      'preferred_name': 'Preferred Name',
      'date_of_birth': 'Date of Birth',
      'phone_number': 'Phone Number',
      'gender': 'Gender',
      'pronouns': 'Pronouns',
      'home_street': 'Home Address',
      'home_city': 'City',
      'home_state': 'State',
      'home_postal_code': 'ZIP Code',
      'home_country': 'Country',
      'citizenship_status': 'Citizenship Status',
      'birth_country': 'Birth Country',
      'first_language': 'First Language',
      'ethnicities': 'Ethnicity Information',
      'military_status': 'Military Status',
      'current_school': 'Current School',
      'graduation_date': 'Graduation Date',
      'gpa': 'GPA',
      'class_rank': 'Class Rank',
      'class_size': 'Class Size',
      'sat_scores': 'SAT Scores',
      'act_scores': 'ACT Scores',
      'ap_tests': 'AP Test Scores',
      'ib_tests': 'IB Test Scores',
      'extracurricular_activities': 'Extracurricular Activities'
    };

    return fieldMappings[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Group imported fields by category for better display
   */
  groupImportedFields(fields: string[]): Record<string, string[]> {
    const groups: Record<string, string[]> = {
      'Personal Information': [],
      'Academic Information': [],
      'Test Scores': [],
      'Activities': []
    };

    const personalFields = [
      'first_name', 'middle_name', 'last_name', 'preferred_name',
      'date_of_birth', 'phone_number', 'gender', 'pronouns',
      'home_street', 'home_city', 'home_state', 'home_postal_code', 'home_country',
      'citizenship_status', 'birth_country', 'first_language', 'ethnicities', 'military_status'
    ];

    const academicFields = [
      'current_school', 'graduation_date', 'gpa', 'class_rank', 'class_size'
    ];

    const testScoreFields = [
      'sat_scores', 'act_scores', 'ap_tests', 'ib_tests', 'sat_subject_tests', 'language_tests'
    ];

    const activityFields = [
      'extracurricular_activities', 'leadership_roles', 'volunteer_work', 'work_experience'
    ];

    fields.forEach(field => {
      if (personalFields.includes(field)) {
        groups['Personal Information'].push(this.formatFieldName(field));
      } else if (academicFields.includes(field)) {
        groups['Academic Information'].push(this.formatFieldName(field));
      } else if (testScoreFields.includes(field)) {
        groups['Test Scores'].push(this.formatFieldName(field));
      } else if (activityFields.includes(field)) {
        groups['Activities'].push(this.formatFieldName(field));
      }
    });

    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  }

  /**
   * Validate email format
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Get security recommendations for users
   */
  getSecurityRecommendations(): string[] {
    return [
      'Your Common App credentials are only used for import and are never stored.',
      'The import process is encrypted and secure.',
      'You can review what data will be imported before confirming.',
      'You can clear imported data at any time if needed.',
      'Import attempts are rate-limited for security.',
      'All import activities are logged for your security.'
    ];
  }
}

export const commonAppService = new CommonAppService();